import { useState, type FormEvent } from 'react';

/**
 * Contact-form email endpoint (API Gateway + SES Lambda). Read at runtime
 * from `/config.json`, a deploy-time artifact written by the `Site` CDK
 * stack (`emailApiUrl`, sourced from the `Email` stack's `apiUrl`). This
 * avoids baking the API Gateway URL into the built JS bundle, so the site
 * always calls whichever API Gateway is currently deployed alongside it.
 * The Lambda expects `{ from, phone, subject, message }`.
 */
interface SiteConfig {
  readonly emailApiUrl?: string;
}

let configPromise: Promise<SiteConfig> | undefined;

function loadConfig(): Promise<SiteConfig> {
  configPromise ??= fetch('/config.json')
    .then((res) => (res.ok ? (res.json() as Promise<SiteConfig>) : {}))
    .catch(() => ({}));
  return configPromise;
}

const randomDigit = () => Math.floor(Math.random() * 10);

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function ContactForm() {
  const [nums, setNums] = useState(() => ({ a: randomDigit(), b: randomDigit() }));
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  const newCaptcha = () => {
    setNums({ a: randomDigit(), b: randomDigit() });
    setCaptcha('');
  };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;

    if (Number(captcha) !== nums.a + nums.b) {
      setError('Your addition was incorrect, please try again.');
      setStatus('error');
      newCaptcha();
      return;
    }

    setStatus('sending');
    setError('');
    try {
      const { emailApiUrl } = await loadConfig();
      if (!emailApiUrl) throw new Error('Missing emailApiUrl in config.json');

      const res = await fetch(emailApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, from: email, phone, message }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setStatus('sent');
    } catch {
      setError('Sorry, your message could not be sent. Please call us at (970) 257-9393.');
      setStatus('error');
      newCaptcha();
    }
  }

  if (status === 'sent') {
    return (
      <div style={{ backgroundColor: 'white', padding: 20, border: 'thin solid black' }}>
        <h3 className="text-center text-success" style={{ margin: 0 }}>
          Message sent
        </h3>
        <p className="text-center" style={{ marginTop: 10 }}>
          Thank you, we will respond as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ backgroundColor: 'white', padding: 10, border: 'thin solid black' }}>
        <div className="row">
          <div className="col-md-3 text-center">Your Email:</div>
          <div className="col-md-9 text-center">
            <input
              id="email"
              className="form-control"
              name="email"
              placeholder="Your email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-3 text-center">Your Phone:</div>
          <div className="col-md-9 text-center">
            <input
              id="phone"
              className="form-control"
              name="phone"
              placeholder="Contact phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-3 text-center">Subject:</div>
          <div className="col-md-9 text-center">
            <input
              id="subject"
              className="form-control"
              name="subject"
              placeholder="Reason for contact"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-3 text-center" style={{ fontWeight: 'bolder' }}>
            Please add these numbers: <span>{nums.a}</span>
            <span style={{ marginLeft: 10, marginRight: 10 }}>+</span>
            <span>{nums.b}</span>
          </div>
          <div className="col-md-9 text-center">
            <input
              id="captcha"
              className="form-control"
              name="captcha"
              placeholder="Please add the numbers"
              type="text"
              inputMode="numeric"
              required
              value={captcha}
              onChange={(e) => setCaptcha(e.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: 5 }} className="row">
          <div className="col-md-3 text-center">Your Question:</div>
          <div className="col-md-9">
            <textarea
              id="message"
              style={{ height: 200 }}
              className="form-control"
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <button
              id="sendEmailBtn"
              type="submit"
              disabled={status === 'sending'}
              style={{ marginTop: 10, color: 'white' }}
              className="btn btn-primary btn-block"
            >
              {status === 'sending' ? 'sending…' : 'send'}
            </button>
          </div>
        </div>
        {error && (
          <div className="alert alert-danger text-center" style={{ marginTop: 10, marginBottom: 0 }}>
            {error}
          </div>
        )}
        <div>
          <small>We will respond as soon as possible</small>
        </div>
      </div>
    </form>
  );
}
